<template>
  <div class='expanded-table-row-content'>
    <h3>Score:</h3>
    <div class="grid-wrapper scores">
      <template v-for="field in scoreFields">
        <label :key="'label-' + field" class="small">{{ field | friendlyDisplayKeys }}</label>
        <star-rating
          :rating="review[field]"
          :size="20"
          :editable="false" />
      </template>
    </div>

    <dl v-if="!editingReason" class="grid-wrapper scores reasons mb-2">
      <dt>Reason for choosing:</dt>
      <dd v-if="review.reasonForChoosingLender !== 'Other'">{{ review.reasonForChoosingLender }}</dd>
      <dd v-else>{{ review.reasonForChoosingLenderCustom }}</dd>

      <button
        v-if="isAdmin"
        class="default-btn link"
        @click="startEditingReason">Edit reason for choosing</button>
    </dl>

    <v-form
      v-if="!!editingReason"
      class="mb-5 mt-2"
      ref="formReasons" @submit.prevent="stopEditingReason">
      <v-select
        v-model="editingReason.reasonForChoosingLender"
        class="mb-2" solo hide-details
        label="Why did you choose the lender?"
        :items="reasonForChoosingLenderOptions" />

      <v-text-field
        v-if="editingReason.reasonForChoosingLender === 'Other'"
        v-model="editingReason.reasonForChoosingLenderCustom"
        solo hide-details
        label="Your reason for choosing lender" />

        <div class="button-group horizonal mt-4">
          <button
            class="default-btn blue mr-5"
            :disabled="saving"
            @click.prevent="stopEditingReason(true)">
            <v-icon v-if="saving" class="mr-2">mdi-loading mdi-spin</v-icon>
            Update
          </button>

          <button
            class="default-btn text"
            @click.prevent="stopEditingReason(false)"
          >Cancel</button>
        </div>
    </v-form>

    <h3>Written Review:</h3>
    <p>{{ review.reviewDescription }}</p>

    <template v-if="!editing">
      <template v-if="!review.responseFrom && !review.responseMessage">
        <button
          class="default-btn link"
          @click="startEditing">Write a Response</button>
      </template>

      <div v-else class="response-wrapper">
        <div class="header">
          <h3>Lender Response</h3>
          <div class="button-group horizontal">
            <button
              class="default-btn text pa-0"
              @click="startEditing">
              <v-icon class="small">mdi-square-edit-outline</v-icon>
            </button>
            <button
              class="default-btn text small"
              @click="clearResponse">
              <v-icon v-if="saving" class="mr-2">mdi-loading mdi-spin</v-icon>
              Delete Reponse
            </button>
          </div>
        </div>

        <p>
          <strong>From:</strong>
          {{ review.responseFrom }}
        </p>
        <p>{{ review.responseMessage }}</p>
      </div>
    </template>

    <v-form
      v-else
      ref="form"
      @submit.prevent="stopEditing(true)"
    >
      <h3>Lender Response</h3>
      <p class="subtitle">
        Write a response to the customers review. Once you submit the reply will appear below the customers review on LendingWell.
      </p>

      <label>Your Name</label>
      <v-text-field
        v-model="editing.responseFrom"
        solo
      />

      <label>Response (400 words max)</label>
      <v-textarea
        v-model="editing.responseMessage"
        solo
        counter="400"
        no-resize
        :rules="[
          v => !v || (v && v.length <= 400) || `Can only be ${400} maximum`,
        ]"
        validate-on-blur
      />

      <div class="button-group horizonal">
        <button
          class="default-btn blue mr-5"
          :disabled="saving"
          @click.prevent="stopEditing(true)">
          <v-icon v-if="saving" class="mr-2">mdi-loading mdi-spin</v-icon>
          Post Response
        </button>

        <button
          class="default-btn text"
          @click.prevent="stopEditing(false)"
        >Cancel</button>
      </div>
    </v-form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'ReviewDetails',
  props: {
    review: {
      type: Object,
      default: () => ({}),
    },
  },

  data: () => ({
    editingReason: null,
    editing: null,
    saving: false,
    scoreFields: [
      'scoreOverall',
      'scoreClarity',
      'scoreGathering',
      'scoreTimeliness',
      'scoreResponsiveness',
    ],

    reasonForChoosingLenderOptions: [
      'Reputation',
      'Rates / Terms',
      'Rejected by another lender',
      'Level of professionalism / communication received',
      'Used previously',
      'Referral',
      'Other',
    ],
  }),

  computed: {
    ...mapGetters('authStore', ['isAdmin']),
  },

  methods: {
    startEditing () {
      if (this.saving) return

      this.editing = {
        responseFrom: this.review.responseFrom || '',
        responseMessage: this.review.responseMessage || '',
      }
    },

    stopEditing (save = false) {
      if (this.saving) return

      if (save) {
        if (!this.$refs.form.validate()) return
        this.saving = true

        this.$emit('edit', {
          responseFrom: this.editing.responseFrom,
          responseMessage: this.editing.responseMessage,
          finished: (success = true) => {
            if (success) this.editing = null
            this.saving = false
          },
        })
      } else {
        this.editing = null
      }
    },

    startEditingReason () {
      if (this.saving) return
      this.editingReason = {
        reasonForChoosingLender: this.review.reasonForChoosingLender,
        reasonForChoosingLenderCustom: this.review.reasonForChoosingLenderCustom,
      }
    },

    stopEditingReason (save = false) {
      if (!save) {
        this.editingReason = null
        return
      }

      if (!this.$refs.formReasons.validate()) return
      this.saving = true

      if (this.editingReason.reasonForChoosingLender !== 'Other') {
        this.editingReason.reasonForChoosingLenderCustom = ''
      }

      this.$emit('edit', {
        reasonForChoosingLender: this.editingReason.reasonForChoosingLender,
        reasonForChoosingLenderCustom: this.editingReason.reasonForChoosingLenderCustom,
        finished: (success = true) => {
          if (success) this.editingReason = null
          this.saving = false
        },
        successMessage: 'The reason for choosing the lender has been updated',
      })
    },

    clearResponse () {
      this.saving = true
      this.$emit('edit', {
        responseFrom: '',
        responseMessage: '',
        successMessage: 'The lender response has been cleared.',
        finished: () => {
          this.saving = false
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.expanded-table-row-content {
  h3 {
    font-family: "Sansation Bold", sans-serif;
    font-weight: bold;
    margin-bottom: 20px;
  }

  .default-btn { margin: 0; }
  .default-btn.link { padding: 0; }

  .response-wrapper {
    margin: 40px 0 0 20px;
    padding: 15px;

    background-color: #E5E5E5;

    p { margin: 10px 0 0; }
    h3 button {
      margin-left: 10px;
      .v-icon { font-size: 20px; }
    }
  }

  form {
    margin-top: 40px;
    padding-left: 20px;
  }

  .button-group {
    justify-content: flex-start;
  }

  .header {
    display: flex;
    align-items: center;
    margin-bottom: 10px;

    h3 { margin-bottom: 0 !important; }

    .button-group {
      margin-left: 10px;
      justify-content: space-between;
      flex-grow: 1;
    }
  }
}

.grid-wrapper.reasons button {
  text-align: left;

  @include gridPos($colS: 1, $colE: 3);
}

</style>
